// frontend/src/admin/pages/AdminCommunicationsPage.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  getAdminStats,
  getUsageReport,
  listUsers,
  sendBroadcast,
  sendEmailToUser,
} from "../api";

const TARGETS = [
  { value: "all", label: "All users" },
  { value: "free", label: "Free plan" },
  { value: "starter", label: "Starter plan" },
  { value: "pro", label: "Pro plan" },
  { value: "business", label: "Business plan" },
];

const PLAN_NAME = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  business: "Business",
};

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 max-w-2xl">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">{title}</h2>
      {children}
    </div>
  );
}

/**
 * AdminCommunicationsPage — broadcast an in-app notification to an audience,
 * or email a single user directly.
 *
 * The broadcast confirm dialog shows an estimated recipient count derived from
 * the dashboard stats (total users, for "all") and the usage report's plan
 * adoption (org count per plan — an approximation, since per-plan *user*
 * counts aren't exposed).
 */
function AdminCommunicationsPage() {
  // ── Broadcast state ──
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [target, setTarget] = useState("all");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Estimation sources (best-effort; null until loaded).
  const [totalUsers, setTotalUsers] = useState(null);
  const [planOrgs, setPlanOrgs] = useState(null); // {planName: orgCount}

  // ── Email-user state ──
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    // Best-effort estimation data — failures just hide the estimate.
    (async () => {
      try {
        const stats = await getAdminStats();
        setTotalUsers(stats.total_users ?? null);
      } catch {
        /* estimate unavailable */
      }
      try {
        const usage = await getUsageReport();
        const map = {};
        for (const p of usage.plan_adoption ?? []) map[p.plan] = p.orgs;
        setPlanOrgs(map);
      } catch {
        /* estimate unavailable */
      }
    })();
  }, []);

  const estimateLabel = () => {
    if (target === "all") {
      return totalUsers != null ? `${totalUsers} users` : "all users";
    }
    const plan = PLAN_NAME[target];
    const orgs = planOrgs?.[plan];
    return orgs != null
      ? `members of ~${orgs} ${plan} org${orgs === 1 ? "" : "s"}`
      : `all ${plan} users`;
  };

  const onSendBroadcast = async (e) => {
    e.preventDefault();
    const ok = window.confirm(
      `Send this broadcast to ${estimateLabel()}? It appears in their in-app notifications immediately.`,
    );
    if (!ok) return;
    setSendingBroadcast(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), target };
      if (actionUrl.trim()) payload.action_url = actionUrl.trim();
      await sendBroadcast(payload);
      toast.success("Broadcast queued.");
      setTitle("");
      setBody("");
      setActionUrl("");
    } catch {
      toast.error("Failed to queue broadcast.");
    } finally {
      setSendingBroadcast(false);
    }
  };

  const onSearchUsers = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    try {
      const data = await listUsers({ q: query.trim(), per_page: 10 });
      setResults(data.items ?? []);
      setSelected(null);
    } catch {
      toast.error("Search failed.");
    }
  };

  const onSendEmail = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSendingEmail(true);
    try {
      await sendEmailToUser({
        user_id: selected.id,
        subject: subject.trim(),
        message: message.trim(),
      });
      toast.success(`Email sent to ${selected.email}.`);
      setSubject("");
      setMessage("");
    } catch {
      toast.error("Failed to send email.");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-4">
        Communications
      </h1>

      {/* Section 1 — Broadcast */}
      <Section title="Broadcast (in-app notification)">
        <form onSubmit={onSendBroadcast} className="space-y-4">
          <div>
            <label
              htmlFor="bc-title"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Title
            </label>
            <input
              id="bc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="bc-body"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Body
            </label>
            <textarea
              id="bc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="bc-url"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Action URL (optional)
            </label>
            <input
              id="bc-url"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="/whatsnew"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="bc-target"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Target audience
            </label>
            <select
              id="bc-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              {TARGETS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="ml-3 text-xs text-slate-500">
              Estimated reach: {estimateLabel()}
            </span>
          </div>
          <button
            type="submit"
            disabled={sendingBroadcast}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500 disabled:opacity-40"
          >
            {sendingBroadcast ? "Queuing…" : "Send Broadcast"}
          </button>
        </form>
      </Section>

      {/* Section 2 — Email User */}
      <Section title="Email a user">
        <form onSubmit={onSearchUsers} className="flex gap-2 mb-4">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email…"
            aria-label="Search users by email"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
          />
          <button
            type="submit"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50"
          >
            Search
          </button>
        </form>

        {results.length > 0 && (
          <ul className="mb-4 divide-y divide-slate-100 border border-slate-200 rounded-md">
            {results.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setSelected(u)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                    selected?.id === u.id
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-slate-700"
                  }`}
                >
                  {u.email}
                  {!u.is_active && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-red-700 bg-red-100 rounded px-1.5 py-0.5">
                      disabled
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={onSendEmail} className="space-y-4">
          <div>
            <label
              htmlFor="em-subject"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Subject
            </label>
            <input
              id="em-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={300}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="em-message"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Message
            </label>
            <textarea
              id="em-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={!selected || sendingEmail}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500 disabled:opacity-40"
          >
            {selected
              ? sendingEmail
                ? "Sending…"
                : `Send Email to ${selected.email}`
              : "Select a user first"}
          </button>
        </form>
      </Section>
    </div>
  );
}

export default AdminCommunicationsPage;
